import { useEffect, useRef, useState } from "preact/hooks";
import {
  calculateCrowdEffectLayout,
  circularPlaybackDrift,
  CROWD_EFFECT_MEDIA_FIT,
  crowdEffectOffsets,
  crowdEffectPlayerKeys,
  crowdEffectPosition,
  type CrowdPlaybackMode,
} from "../core/effects/crowdEffectLayout";
import { logger } from "../core/telemetry/logger";
import type { RoomEffect } from "../models/contracts";

interface CrowdEffectWallProps {
  effect: RoomEffect;
  serverNow: number;
  playbackMode?: CrowdPlaybackMode;
}

const effectStartTime = (effect: RoomEffect, fallback: number): number => {
  const scheduled = Date.parse(effect.scheduledStartUtc);
  if (Number.isFinite(scheduled)) return scheduled;
  const serverTime = Date.parse(effect.serverTime);
  return Number.isFinite(serverTime) ? serverTime : fallback;
};

export function CrowdEffectWall({ effect, serverNow, playbackMode = "offset" }: CrowdEffectWallProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const serverOffsetRef = useRef(serverNow - Date.now());
  const loggedFailure = useRef(false);
  const [layout, setLayout] = useState(() => calculateCrowdEffectLayout(window.innerWidth, window.innerHeight));
  serverOffsetRef.current = serverNow - Date.now();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const update = () => {
      const bounds = container.getBoundingClientRect();
      setLayout(calculateCrowdEffectLayout(bounds.width, bounds.height));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => { loggedFailure.current = false; }, [effect.eventId]);

  const startedAt = effectStartTime(effect, serverNow);
  const source = effect.playbackUrl;
  const playerKeys = crowdEffectPlayerKeys(effect.eventId, layout.count);
  const failed = () => {
    if (loggedFailure.current) return;
    loggedFailure.current = true;
    logger.error("CROWD_EFFECT_MEDIA_FAILED", { effectId: effect.effectId, eventId: effect.eventId });
    window.dispatchEvent(new Event("mojvibe:scene-failed"));
  };

  return <div ref={containerRef} class="scene crowd-effect-wall" data-effect-renderer="crowd">
    {source ? <div class="crowd-effect-panels" style={{ gap: `${layout.gap}px` }}>
      {playerKeys.map((key, index) =>
        <CrowdEffectPlayer
          key={key}
          source={source}
          index={index}
          count={layout.count}
          startedAt={startedAt}
          serverOffsetRef={serverOffsetRef}
          playbackMode={playbackMode}
          width={layout.playerWidth}
          height={layout.playerHeight}
          onFailure={failed}
        />)}
    </div> : <div class="burst" />}
  </div>;
}

interface CrowdEffectPlayerProps {
  source: string;
  index: number;
  count: number;
  startedAt: number;
  serverOffsetRef: { current: number };
  playbackMode: CrowdPlaybackMode;
  width: number;
  height: number;
  onFailure(): void;
}

function CrowdEffectPlayer({ source, index, count, startedAt, serverOffsetRef, playbackMode, width, height, onFailure }: CrowdEffectPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  const synchronize = () => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;
    const offset = crowdEffectOffsets(video.duration, count, playbackMode)[index] ?? 0;
    const expected = crowdEffectPosition(Date.now() + serverOffsetRef.current, startedAt, video.duration, offset);
    if (circularPlaybackDrift(video.currentTime, expected, video.duration) > 0.2) video.currentTime = expected;
    void video.play().catch(() => undefined);
  };

  useEffect(() => {
    let timer: number | null = null;
    const correctDrift = () => {
      synchronize();
      timer = window.setTimeout(correctDrift, 5_000);
    };
    synchronize();
    timer = window.setTimeout(correctDrift, 5_000);
    return () => { if (timer !== null) window.clearTimeout(timer); };
  }, [count, index, playbackMode, source, startedAt]);

  return <div class="crowd-effect-panel" style={{ width: `${width}px`, height: `${height}px` }}>
    <video
      ref={videoRef}
      src={source}
      autoplay
      loop
      muted
      playsInline
      preload="auto"
      style={{ objectFit: CROWD_EFFECT_MEDIA_FIT }}
      aria-hidden="true"
      tabIndex={-1}
      data-crowd-offset-index={index}
      onLoadedMetadata={synchronize}
      onCanPlay={synchronize}
      onDurationChange={synchronize}
      onError={onFailure}
    />
  </div>;
}
